import { ref } from "vue";

/**
 * Toast notification system for segment_web. Lightweight single-slot
 * design — newer toasts replace older ones rather than stacking.
 *
 * Reimplemented locally per design doc C3 (no imports from
 * clips_review_desktop). Shape is similar but trimmed: only the toast
 * variants segment_web actually needs.
 */

export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  /**
   * Unique per-toast identifier. The current single-slot design
   * doesn't strictly need it (the parent's dismiss handler clears
   * `currentToast` unconditionally), but it's plumbed through the
   * `dismiss` emit so a future stacking design can target a specific
   * toast without rewriting the ToastNotification contract.
   */
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 5000,
  error: 6000,
};

const currentToast = ref<Toast | null>(null);
let counter = 0;

export function useToast() {
  function show(message: string, type: ToastType = "info", duration?: number): void {
    counter += 1;
    currentToast.value = {
      id: `${Date.now()}-${counter}`,
      message,
      type,
      duration: duration ?? DEFAULT_DURATIONS[type],
    };
  }

  return {
    currentToast,
    showSuccess: (m: string, d?: number) => show(m, "success", d),
    showError: (m: string, d?: number) => show(m, "error", d),
    showInfo: (m: string, d?: number) => show(m, "info", d),
    showWarning: (m: string, d?: number) => show(m, "warning", d),
    dismiss: () => {
      currentToast.value = null;
    },
    /** Test-only: reset module state between tests. */
    _resetForTests: () => {
      currentToast.value = null;
      counter = 0;
    },
  };
}
